import React, { useEffect } from 'react';
import pt from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';

import workflowRuns, { getActionRun, getRunKey } from 'actions/workflow-runs';
import { formatDate } from 'utils';

import Loading from 'components/loading';
import Tooltip from 'components/tooltip';

import './action-run.scss';

/**
 * Finds the runs of app actions still active from before a reload, or started
 * by another user, once for each action and record
 * @param {object} options
 * @param {Array<object>} options.actions - app actions with id and app
 * @param {string} [options.recordId]
 * @param {Function} [options.onComplete] - refreshes the page when a restored
 * run finishes
 */
export function useRestoreActionRuns({
  actions: appActions,
  recordId,
  onComplete,
}) {
  const dispatch = useDispatch();

  const restoreKey = appActions
    .map((action) => getRunKey(action.app?.id, action.id, recordId))
    .join(',');

  useEffect(() => {
    const actionsByApp = groupBy(
      appActions.filter((action) => action.app),
      (action) => action.app.id,
    );

    for (const actions of Object.values(actionsByApp)) {
      dispatch(
        workflowRuns.restore({
          app: actions[0].app,
          actions,
          recordId,
          onComplete,
        }),
      );
    }
    // Restored once for each action and record
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreKey]);
}

/**
 * Status of the latest workflow run started by app actions: running with its
 * current step, failed with the error, or canceled. On mount it finds a run
 * still active from before a reload, or started by another user.
 */
export default function AppActionRunStatus({
  actions: appActions,
  recordId,
  onComplete,
}) {
  const runs = useSelector((state) => state.workflowRuns);

  useRestoreActionRuns({ actions: appActions, recordId, onComplete });

  const actionRuns = appActions.map((action) =>
    getActionRun(runs, action.app?.id, action.id, recordId),
  );

  // An active run first, then the latest
  const run = sortBy(actionRuns.filter(Boolean), [
    (run) => run.status === 'active',
    'date_created',
  ]).pop();

  // A completed run shows nothing
  if (!run || run.status === 'completed') {
    return null;
  }

  return (
    <div className="app-action-run-status button button-sm button-cancel">
      {run.status === 'active' ? (
        <>
          Running{run.current_step && ` · ${run.current_step}`}
          <Loading className="loading-icon" />
        </>
      ) : run.status === 'failed' ? (
        <span className="muted">
          <Tooltip message={run.error}>Failed</Tooltip>
        </span>
      ) : (
        <span className="muted">Canceled</span>
      )}
    </div>
  );
}

AppActionRunStatus.propTypes = {
  // App actions with id, label and app
  actions: pt.array.isRequired,
  recordId: pt.string,
  // Refreshes the page when a restored run finishes
  onComplete: pt.func,
};

/**
 * Finds runs active before the page loaded, or started by another user, so
 * their actions show as running and the page refreshes when they finish.
 * Renders nothing.
 */
export function AppActionRunsRestore(props) {
  useRestoreActionRuns(props);

  return null;
}

AppActionRunsRestore.propTypes = {
  // App actions with id, label and app
  actions: pt.array.isRequired,
  recordId: pt.string,
  // Refreshes the page when a restored run finishes
  onComplete: pt.func,
};

/**
 * Help for an action that cannot run again while its run is active
 * @param {object} [run]
 * @returns {string|null}
 */
export function getRunningHelp(run) {
  return run?.status === 'active'
    ? `Already running since ${formatDate(run.date_created, 'age')}`
    : null;
}

/**
 * Renders an app action disabled, with help, while its run is active
 */
export function AppActionRunGuard({ action, recordId, children }) {
  const run = useSelector((state) =>
    getActionRun(state.workflowRuns, action.app?.id, action.id, recordId),
  );
  const help = !action.disabled && getRunningHelp(run);

  return children(help ? { ...action, disabled: true, help } : action);
}

AppActionRunGuard.propTypes = {
  // App action with id and app, and disabled and help when it cannot run
  action: pt.object.isRequired,
  recordId: pt.string,
  // Renders the action
  children: pt.func.isRequired,
};
